import type {
  CmsNewsItem,
  FrontNewsDetail,
  FrontNewsListItem,
  FrontNewsNeighborRef,
  FrontProductCenterData,
  FrontProductCenterProduct,
  FrontSuccessCaseItem,
  HomeSectionItem,
  JobItem,
  ProductItem,
  ProductPrimaryCategory,
  ProductSecondaryCategory,
  RouteMenuItem,
  SolutionItem
} from '../api/types/cms';

export type NavItemView = {
  to: string;
  label: string;
};

/** 前台顶栏导航：path → 路径，remark → 展示名 */
export function mapRoutesToNavItems(routes: RouteMenuItem[]): NavItemView[] {
  console.log(routes);
  return routes
    .map((r) => ({
      to: (r.path || '').trim() || '/',
      label: (r.remark || r.moduleKey || '').trim()
    }))
    .filter((item) => item.to && item.label);
}

export type HeroSlideView = {
  title: string;
  subtitle: string;
  tags: string[];
  image: string;
  linkText?: string;
  linkUrl?: string;
  phoneText?: string;
  phone?: string;
};

export type SolutionCardView = {
  id: string;
  title: string;
  image: string;
  /** 展示用介绍行（introLine1~3，已去前缀） */
  lines: string[];
  phone?: string;
};

export type NewsCardView = {
  id: string;
  title: string;
  summary: string;
  date: string;
  thumbUrl: string;
};

export type NewsDetailView = {
  id: string;
  title: string;
  author: string;
  summary: string;
  date: string;
  thumbUrl: string;
  contentUrl: string;
  previewUrl?: string;
  sourceWechatUrl?: string;
};

export type NewsNeighborView = {
  id: string;
  title: string;
};

export type JobPostView = {
  id: string;
  title: string;
  bullets: string[];
  email?: string;
};

export function mapBannersToHeroSlides(banners: HomeSectionItem[]): HeroSlideView[] {
  return banners
    .map((b) => ({
      title: b.title,
      subtitle: b.intro ?? '',
      tags: b.tags ?? [],
      image: b.backgroundImageUrl || b.imageUrl || '',
      linkText: b.linkText,
      linkUrl: b.linkUrl,
      phoneText: b.phoneText,
      phone: b.phone
    }))
    .filter((s) => s.title || s.image);
}

function introLineText(line?: string): string {
  if (!line?.trim()) {
    return '';
  }
  return line.trim();
}

function solutionCoverUrl(item: SolutionItem): string {
  const fromUrls = item.imageUrls?.map((u) => u?.trim()).find(Boolean);
  if (fromUrls) {
    return fromUrls;
  }
  return item.images?.map((img) => img.imageUrl?.trim()).find(Boolean) ?? '';
}

export function mapSolutionsToCards(items: SolutionItem[]): SolutionCardView[] {
  return items.map((s) => {
    const line1 = introLineText(s.introLine1) || introLineText(s.intro);
    const line2 = introLineText(s.introLine2);
    const line3 = introLineText(s.introLine3);
    const lines = [line1, line2, line3].filter(Boolean);

    return {
      id: String(s.id ?? s.title),
      title: s.title,
      image: solutionCoverUrl(s),
      lines,
      phone: s.phone?.trim() || undefined
    };
  });
}

export function mapFrontNewsDetail(detail: FrontNewsDetail): NewsDetailView {
  return {
    id: String(detail.id),
    title: detail.title,
    author: detail.author ?? '',
    summary: detail.digest ?? '',
    date: formatNewsDate(detail.publishTimeDisplay),
    thumbUrl: detail.thumbUrl ?? '',
    contentUrl: detail.contentUrl ?? detail.previewUrl ?? '',
    previewUrl: detail.previewUrl,
    sourceWechatUrl: detail.sourceWechatUrl
  };
}

/** 详情接口 prev / next → 页脚导航（id 为 0 或空视为无） */
export function mapNewsNeighborRef(ref?: FrontNewsNeighborRef | null): NewsNeighborView | null {
  if (!ref) return null;
  const title = ref.title?.trim();
  const id = ref.id;
  if (!title || id === undefined || id === null || id === '' || id === 0 || id === '0') {
    return null;
  }
  return { id: String(id), title };
}

export function mapNewsNeighborsFromDetail(
  detail: FrontNewsDetail
): { prev: NewsNeighborView | null; next: NewsNeighborView | null } {
  return {
    prev: mapNewsNeighborRef(detail.prev),
    next: mapNewsNeighborRef(detail.next)
  };
}

export function mapFrontNewsToCards(items: FrontNewsListItem[]): NewsCardView[] {
  return items.map((n) => ({
    id: String(n.id),
    title: n.title,
    summary: n.digest ?? '',
    date: formatNewsDate(n.publishTimeDisplay),
    thumbUrl: n.thumbUrl ?? ''
  }));
}

/** 兼容管理端 / 详情等 CmsNewsItem 结构 */
export function mapNewsToCards(items: CmsNewsItem[]): NewsCardView[] {
  return items.map((n) => ({
    id: String(n.id ?? n.wechatDedupeKey ?? n.title ?? ''),
    title: n.title ?? '',
    summary: n.digest ?? '',
    date: formatNewsDate(n.wechatPublishTimeDisplay ?? n.updatedAt ?? n.lastSyncAt),
    thumbUrl: n.thumbQiniuUrl ?? n.previewQiniuUrl ?? ''
  }));
}

export function mapJobDetailToPost(job: JobItem): JobPostView {
  const bullets: string[] = [];
  if (job.intro?.trim()) {
    bullets.push(...formatJobSection('岗位职责', job.intro));
  }
  if (job.requirements?.trim()) {
    bullets.push(...formatJobSection('任职要求', job.requirements));
  }
  if (!bullets.length) {
    bullets.push('岗位职责与任职要求详见招聘说明。');
  }
  return {
    id: String(job.id ?? job.title),
    title: job.title,
    bullets,
    email: job.email?.trim() || undefined
  };
}

export function mapJobsToPosts(items: JobItem[]): JobPostView[] {
  return items.map((j) => mapJobDetailToPost(j));
}

function formatJobSection(heading: string, text: string): string[] {
  const lines = text
    .split(/\r?\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) {
    return [];
  }
  return [heading, ...lines];
}

export type ProductCardView = {
  id: string;
  title: string;
  image: string;
  advantage: string;
  scenario: string;
  capability: string;
  detailLines: string[];
  phone?: string;
};

export type ProductSeriesView = {
  id: string;
  tabLabel: string;
  products: ProductCardView[];
};

export type ProductCategoryNavView = {
  id: string;
  name: string;
  products: ProductCardView[];
};

export type ProductTypeNavView = {
  id: string;
  name: string;
  categories: ProductCategoryNavView[];
};

export type ProductCenterNavView = {
  types: ProductTypeNavView[];
};

function mapProductItem(p: ProductItem): ProductCardView {
  const specs = p.specs ?? [];
  return {
    id: String(p.id ?? p.title),
    title: p.title || p.name || '',
    image: p.coverUrl ?? p.detailImages?.[0]?.imageUrl ?? '',
    advantage: p.intro ?? p.summary ?? specs[0]?.specValue ?? '',
    scenario: specs[1]?.specValue ?? p.intro ?? '',
    capability: specs[2]?.specValue ?? specs[0]?.specValue ?? '',
    detailLines: specs.map((s) => `${s.specKey}：${s.specValue}`).filter(Boolean)
  };
}


function mapFrontProductItem(p: FrontProductCenterProduct): ProductCardView {
  const line1 = (p.introLine1) || (p.intro);
  const line2 = (p.introLine2);
  const line3 = (p.introLine3);
  const legacyLines = (p.intro ?? '')
    .split(/\r?\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  const advantage = line1 || legacyLines[0] || '';
  const scenario = line2 || legacyLines[1] || '';
  const capability = line3 || legacyLines[2] || '';
  const detailLines = [p.introLine1, p.introLine2, p.introLine3, p.intro]
    .map((line) => line?.trim())
    .filter((line): line is string => Boolean(line));

  return {
    id: String(p.id),
    title: p.title,
    image: p.coverUrl ?? '',
    advantage,
    scenario,
    capability,
    detailLines: detailLines.length ? detailLines : [],
    phone: p.phone?.trim() || undefined
  };
}

/** 产品中心：保留 types → categories → products 两级导航结构 */
export function mapProductCenterToNav(data: FrontProductCenterData | null): ProductCenterNavView {
  if (!data?.types?.length) {
    return { types: [] };
  }

  const types = data.types.map((type) => ({
    id: String(type.id ?? type.code ?? type.name),
    name: type.name,
    categories: (type.categories ?? []).map((cat) => ({
      id: String(cat.id ?? cat.name),
      name: cat.name,
      products: (cat.products ?? []).map((item) => mapFrontProductItem(item))
    }))
  }));

  return { types };
}

/** @deprecated 使用 mapProductCenterToNav */
export function mapProductCenterToSeries(data: FrontProductCenterData | null): ProductSeriesView[] {
  const nav = mapProductCenterToNav(data);
  return nav.types
    .map((type) => ({
      id: type.id,
      tabLabel: type.name,
      products: type.categories.flatMap((cat) => cat.products)
    }))
    .filter((series) => series.products.length > 0);
}

/** @deprecated 使用 mapProductCenterToSeries */
export function mapProductsToSeries(
  products: ProductItem[],
  categories: (ProductPrimaryCategory | ProductSecondaryCategory)[]
): ProductSeriesView[] {
  if (!products.length) {
    return [];
  }

  const primaries = categories.filter((c) => !('typeId' in c && c.typeId)) as ProductPrimaryCategory[];
  const secondaries = categories.filter((c) => 'typeId' in c) as ProductSecondaryCategory[];

  if (!primaries.length && !secondaries.length) {
    return [
      {
        id: 'all',
        tabLabel: '产品系列',
        products: products.map(mapProductItem)
      }
    ];
  }

  const seriesFromPrimary = primaries.map((primary) => {
    const childIds = new Set(
      secondaries.filter((s) => String(s.typeId ?? s.parentId) === String(primary.id)).map((s) => String(s.id))
    );
    const seriesProducts = products.filter((p) => {
      const cid = String(p.categoryId ?? p.secondaryCategoryId ?? '');
      return childIds.has(cid) || String(p.primaryCategoryId) === String(primary.id);
    });
    return {
      id: String(primary.id ?? primary.code ?? primary.name),
      tabLabel: primary.name,
      products: seriesProducts.map(mapProductItem)
    };
  });

  const nonEmpty = seriesFromPrimary.filter((s) => s.products.length);
  return nonEmpty.length ? nonEmpty : [{ id: 'all', tabLabel: '产品系列', products: products.map(mapProductItem) }];
}

const CASE_GALLERY_SLOTS = 4;

export type CaseShowcaseView = {
  id: string;
  tabLabel: string;
  title: string;
  description: string;
  backgroundUrl: string;
  /** 有效配图 URL，0~4 张，有几张展示几张 */
  shots: string[];
  /** 案例标签，如「服务器部署」 */
  tags?: string[];
};

export function mapSuccessCasesToShowcases(items: FrontSuccessCaseItem[]): CaseShowcaseView[] {
  return items.map((item) => ({
    id: String(item.id),
    title: item.title,
    tabLabel: item.title,
    description: item.intro ?? '',
    backgroundUrl: item.backgroundUrl ?? '',
    shots: normalizeCaseShots(item.imageUrls ?? []),
    tags: (item.tags ?? []).map((t) => t?.trim()).filter(Boolean)
  }));
}

function normalizeCaseShots(urls: string[]): string[] {
  return urls
    .map((u) => u?.trim())
    .filter(Boolean)
    .slice(0, CASE_GALLERY_SLOTS) as string[];
}

function formatNewsDate(raw?: string): string {
  if (!raw) {
    return '';
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw.slice(0, 10);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
